import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { RotateCcw, Store, User, Loader2 } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/** Admin — deactivated (soft-deleted) user accounts + restaurants, with restore. */
export default function DeletedAccountsPanel() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState({});

  const load = () => {
    apiClient.get("/admin/deleted-accounts").then(({ data }) => setData(data)).catch(() => setData({ users: [], restaurants: [] }));
  };
  useEffect(load, []);

  const restore = async (kind, id) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const url = kind === "user" ? `/admin/users/${id}/restore` : `/admin/restaurants/${id}/restore`;
      const { data: res } = await apiClient.post(url);
      toast.success(`Compte restauré : ${res.restored?.name || res.restored?.email || id}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  if (!data) return <div className="text-mute text-sm py-10">Chargement…</div>;

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

  return (
    <div className="space-y-6" data-testid="deleted-accounts-panel">
      <div className="bg-white border border-sand rounded-2xl p-5">
        <h3 className="font-display text-lg font-semibold mb-1 inline-flex items-center gap-2"><User className="w-4 h-4" /> {t("admin.delUsersTitle")}</h3>
        <p className="text-xs text-mute mb-4">{t("admin.delUsersHint")}</p>
        {data.users.length === 0 ? (
          <div className="text-sm text-mute py-4 text-center" data-testid="deleted-users-empty">{t("admin.delUsersEmpty")}</div>
        ) : (
          <div className="space-y-2">
            {data.users.map((u) => (
              <div key={u.user_id} className="flex items-center justify-between gap-3 border border-sand rounded-xl px-3 py-2" data-testid={`deleted-user-${u.user_id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{u.display_name || t("admin.deletedAccount")} <span className="text-[10px] uppercase tracking-widest text-brand-red ml-1">{u.role}</span></div>
                  <div className="text-xs text-mute truncate">{u.display_email} · désactivé le {fmtDate(u.deleted_at)}</div>
                </div>
                <button
                  onClick={() => restore("user", u.user_id)}
                  disabled={busy[u.user_id] || !u.restorable}
                  title={u.restorable ? "Réactiver ce compte" : "Compte anonymisé sans sauvegarde — non restaurable"}
                  data-testid={`restore-user-${u.user_id}`}
                  className="inline-flex items-center gap-1.5 text-xs rounded-full bg-brand-green hover:bg-[#00804E] disabled:opacity-40 text-white px-3 py-1.5 font-semibold shrink-0"
                >
                  {busy[u.user_id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Restaurer
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-sand rounded-2xl p-5">
        <h3 className="font-display text-lg font-semibold mb-1 inline-flex items-center gap-2"><Store className="w-4 h-4" /> {t("admin.delRestosTitle")}</h3>
        <p className="text-xs text-mute mb-4">La restauration remet le restaurant en ligne avec son menu.</p>
        {data.restaurants.length === 0 ? (
          <div className="text-sm text-mute py-4 text-center" data-testid="deleted-restos-empty">{t("admin.delRestosEmpty")}</div>
        ) : (
          <div className="space-y-2">
            {data.restaurants.map((r) => (
              <div key={r.restaurant_id} className="flex items-center justify-between gap-3 border border-sand rounded-xl px-3 py-2" data-testid={`deleted-resto-${r.restaurant_id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{r.name}</div>
                  <div className="text-xs text-mute">{r.zone} · supprimé le {fmtDate(r.deleted_at)}</div>
                </div>
                <button
                  onClick={() => restore("restaurant", r.restaurant_id)}
                  disabled={busy[r.restaurant_id]}
                  data-testid={`restore-resto-${r.restaurant_id}`}
                  className="inline-flex items-center gap-1.5 text-xs rounded-full bg-brand-green hover:bg-[#00804E] disabled:opacity-40 text-white px-3 py-1.5 font-semibold shrink-0"
                >
                  {busy[r.restaurant_id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Restaurer
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
